
import Vue from "vue";
import { UI } from "@/store/ui";
import { LOYALTY } from "@/store/loyalty";
import { createNamespacedHelpers } from "vuex";
import { ILoyaltyState, OfferLevelType, IChannel } from "@/store/loyalty/types";

import { required } from "vuelidate/lib/validators";
import { onlyDigits, multipleOfTen } from "@/validators";
import { LOYALTY_ROUTE_NAMES } from "@/loyalty-routes";
import isEqual from "lodash/isEqual";
import { LOYALTY_BASE_URL } from "../../helpers/config";
import { accountOfferAccess } from "@/helpers/loyaltyEditAccess";

const { mapGetters, mapState } = createNamespacedHelpers(LOYALTY.NAME_SPACE);

export default Vue.extend({
  props: {
    isEditing: {
      type: Boolean
    }
  },
  computed: {
    hasEditAccess() {
      return accountOfferAccess();
    },
    isLoading(): boolean {
      const { ui, loyalty } = this.$store.state;

      return ui.isloading || loyalty.isLoading;
    },
    isFormDisabled() {
      return !this.hasEditAccess || !this.isEligible || this.isLoading;
    },
    canSave() {
      return !this.$v.$invalid && this.hasEditAccess && !this.isLoading;
    },
    hasChanges(): boolean {
      const changes = this.$store.state.loyalty.accountOffer;
      const original = this.$store.state.loyalty.accountOfferOriginal;

      return !isEqual(original, changes);
    },
    isEligible: {
      get() {
        return this.accountOffer && this.accountOffer.isEligible;
      },
      set(value: boolean) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER_IS_ELIGIBLE),
          value
        );
      }
    },
    offerLevelType: {
      get() {
        return this.accountOffer && this.accountOffer.level;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER_LEVEL_TYPE),
          value
        );
      }
    },
    flyBuysEarnRateQuantity: {
      get() {
        return this.accountOffer && this.accountOffer.flyBuysEarnRateQuantity;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(
            LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER_FLYBUYS_EARN_RATE_QUANTITY
          ),
          value
        );
      }
    },
    airpointsEarnRateQuantity: {
      get() {
        return this.accountOffer && this.accountOffer.airpointsEarnRateQuantity;
      },
      set(value: string) {
        this.$store.commit(
          LOYALTY.NS(
            LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER_AIRPOINTS_EARN_RATE_QUANTITY
          ),
          value
        );
      }
    },
    channels: {
      get() {
        return this.accountOffer && this.accountOffer.channels;
      },
      set(value: any[]) {
        this.$store.commit(
          LOYALTY.NS(LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER_CHANNELS),
          value
        );
      }
    },
    offerLevelTypeList: () => OfferLevelType,
    ...mapState({
      channelList: (state: ILoyaltyState) => state.channelList,
      accountOffer: (state: ILoyaltyState) => state.accountOffer
    })
  },
  methods: {
    onCancelChanges() {
      const originalData = this.$store.state.loyalty.accountOfferOriginal;

      this.$store.commit(
        LOYALTY.NS(LOYALTY.MUTATIONS.SET_ACCOUNT_OFFER),
        originalData
      );
    },
    async onSaveChanges() {
      this.$store.dispatch(LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING), true);
      const success = await this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.UPDATE_ACCOUNT_OFFER)
      );

      this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING),
        false
      );

      if (success) {
        this.$router.push({
          name: LOYALTY_ROUTE_NAMES.ACCOUNT_OFFERS
        });
      }
    },
    async onDeleteOffer() {
      this.$store.dispatch(LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING), true);

      const success = await this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.DELETE_ACCOUNT_OFFER)
      );

      this.$store.dispatch(
        LOYALTY.NS(LOYALTY.ACTIONS.TOGGLE_IS_LOADING),
        false
      );

      if (success) {
        this.$router.push({
          name: LOYALTY_ROUTE_NAMES.ACCOUNT_OFFERS
        });
      }
    }
  },
  validations() {
    // only do validation when isEligible
    if (this.isEligible) {
      return {
        offerLevelType: {
          required
        },
        channels: {
          required
        },
        flyBuysEarnRateQuantity: {
          required,
          onlyDigits,
          multipleOfTen
        },
        airpointsEarnRateQuantity: {
          required,
          onlyDigits,
          multipleOfTen
        }
      };
    } else {
      return {};
    }
  }
});
